import { useEffect, useCallback, useRef } from 'react';

type KeyHandler = (event: KeyboardEvent) => void;

interface HotkeyOptions {
  enabled?: boolean;
  preventDefault?: boolean;
  enableOnFormElements?: boolean;
}

const isFormElement = (element: Element | null): boolean => {
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    (element as HTMLElement).isContentEditable
  );
};

export const parseHotkey = (hotkey: string): { key: string; ctrl: boolean; meta: boolean; alt: boolean; shift: boolean } => {
  const parts = hotkey.toLowerCase().split('+');
  return {
    key: parts[parts.length - 1],
    ctrl: parts.includes('ctrl'),
    meta: parts.includes('meta') || parts.includes('cmd'),
    alt: parts.includes('alt'),
    shift: parts.includes('shift'),
  };
};

export const matchesHotkey = (event: KeyboardEvent, hotkey: string): boolean => {
  const parsed = parseHotkey(hotkey);
  const key = event.key.toLowerCase();

  // Handle special keys
  const keyMatches = key === parsed.key ||
    (parsed.key === 'escape' && key === 'escape') ||
    (parsed.key === 'esc' && key === 'escape') ||
    (parsed.key === 'enter' && key === 'enter') ||
    (parsed.key === 'space' && key === ' ') ||
    (parsed.key === '/' && key === '/') ||
    (parsed.key === '?' && key === '?');

  return (
    keyMatches &&
    event.ctrlKey === parsed.ctrl &&
    (event.metaKey === parsed.meta || (parsed.meta && event.ctrlKey)) && // Cmd on Mac = Ctrl on Win
    event.altKey === parsed.alt &&
    event.shiftKey === parsed.shift
  );
};

export const useHotkey = (
  hotkey: string | string[],
  handler: KeyHandler,
  options: HotkeyOptions = {}
): void => {
  const { enabled = true, preventDefault = true, enableOnFormElements = false } = options;
  const handlerRef = useRef(handler);

  // Always use the latest handler
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const hotkeys = Array.isArray(hotkey) ? hotkey : [hotkey];

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if in form element and not explicitly enabled
      if (!enableOnFormElements && isFormElement(event.target as Element)) {
        return;
      }

      for (const hk of hotkeys) {
        if (matchesHotkey(event, hk)) {
          if (preventDefault) {
            event.preventDefault();
            event.stopPropagation();
          }
          handlerRef.current(event);
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hotkey, enabled, preventDefault, enableOnFormElements]);
};

// Sequence hotkey support (e.g., "g h" for go to home)
export const useSequenceHotkey = (
  sequence: string[],
  handler: KeyHandler,
  options: HotkeyOptions = {}
): void => {
  const { enabled = true, preventDefault = true, enableOnFormElements = false } = options;
  const sequenceIndex = useRef(0);
  const lastKeyTime = useRef(0);
  const handlerRef = useRef(handler);
  const SEQUENCE_TIMEOUT = 1000; // 1 second to complete sequence

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!enableOnFormElements && isFormElement(event.target as Element)) {
        sequenceIndex.current = 0;
        return;
      }

      const now = Date.now();

      // Reset sequence if too much time has passed
      if (now - lastKeyTime.current > SEQUENCE_TIMEOUT) {
        sequenceIndex.current = 0;
      }

      const expectedKey = sequence[sequenceIndex.current];
      if (matchesHotkey(event, expectedKey)) {
        lastKeyTime.current = now;
        sequenceIndex.current++;

        if (sequenceIndex.current === sequence.length) {
          if (preventDefault) {
            event.preventDefault();
            event.stopPropagation();
          }
          handlerRef.current(event);
          sequenceIndex.current = 0;
        }
      } else {
        // Wrong key - reset sequence
        sequenceIndex.current = 0;
        // But check if it's the start of a new sequence
        if (matchesHotkey(event, sequence[0])) {
          lastKeyTime.current = now;
          sequenceIndex.current = 1;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sequence, enabled, preventDefault, enableOnFormElements]);
};

export default useHotkey;
